// Amelia SSO gate for the IAS deployment. Wraps the app in the vendor
// SsoProvider, which owns the OIDC redirect dance; on success we hand the
// access token to the backend via `/api/oidc-auth` (see `session-api`) and
// establish a Metabase session cookie.

import {
  SsoProvider,
  type SsoProviderProps,
} from "@intelisysaviation/amelia-sso-client-react";
import { type ReactNode, useCallback, useMemo } from "react";
import { push } from "react-router-redux";
import { t } from "ttag";

import { logout } from "metabase/redux/auth";
import { useDispatch } from "metabase/redux/hooks";

import { hasMetabaseUserInfo } from "./app-utils";
import { getConfigSettings } from "./configs";
import { iasExpireSession } from "./expire-session";
import { iasSessionApi } from "./session-api";

export const AppSsoProvider = ({ children }: { children: ReactNode }) => {
  const configs = getConfigSettings();
  const dispatch = useDispatch();

  const signInCallback = useCallback(
    async (oidcAccessToken: string) => {
      try {
        await dispatch(
          iasSessionApi.endpoints.createSessionWithOidcAuth.initiate({
            oidcAccessToken,
          }),
        ).unwrap();
      } catch (err: unknown) {
        const { status, data } = err as {
          status?: number;
          data?: { errors?: { account?: string } } | string;
        };
        if (status === 403) {
          dispatch(push("/access-denied"));
        } else {
          const errorData =
            typeof data === "object" ? data?.errors?.account : data;
          const description = errorData ?? t`Something went wrong`;
          const error =
            status === 401 ? "AUTHENTICATION_ERROR" : "UNKNOWN_ERROR";
          dispatch(push(`/error?error=${error}&description=${description}`));
        }
        // Re-throw so the SSO client doesn't navigate further.
        throw err;
      }
    },
    [dispatch],
  );

  const ssoProviderProps: SsoProviderProps = useMemo(
    () => ({
      ssoApiBaseUrl: configs.ssoApiBaseUrl,
      clientShortName: configs.clientShortName,
      stage: configs.appStage,
      redirectPath: "/",
      postLogoutRedirectPath: "/auth/login",
      onSigninCallback: signInCallback,
      onSignoutCallback: async () => {
        await dispatch(logout());
      },
      onSigninSilentCallback: async (
        isAuthenticated: boolean,
        sessionExpired: boolean,
        login: () => Promise<void>,
      ) => {
        // Priority order matters. `sessionExpired` must win over the
        // silent-login branch — the SDK's cached `isAuthenticated` can still
        // read `true` for a request or two after the upstream `/sessions/status`
        // starts returning 401, so if we check the silent-login branch first
        // we'll silently re-authenticate the user right past the expired
        // dialog we want to show.
        if (sessionExpired) {
          // `iasExpireSession` kills the Metabase cookie and opens the modal
          // WITHOUT the `window.location.reload()` that upstream's `logout`
          // thunk ends with — a reload would wipe the redux state and drop
          // the user on `/auth/login` before the modal ever paints.
          await dispatch(iasExpireSession());
          return;
        }
        if (isAuthenticated && !hasMetabaseUserInfo()) {
          // Signed into SSO but not into Metabase yet. Terminal pages
          // (/access-denied after an OIDC 403, /error after a generic OIDC
          // failure) match this state permanently — bouncing them through
          // login would just loop back here. `/setup` is the one path
          // upstream still redirects to login for, so leave that alone.
          const path = window.location.pathname;
          if (path === "/access-denied" || path.startsWith("/error")) {
            return;
          }
          await login();
        }
      },
    }),
    [
      configs.ssoApiBaseUrl,
      configs.clientShortName,
      configs.appStage,
      signInCallback,
      dispatch,
    ],
  );

  return <SsoProvider {...ssoProviderProps}>{children}</SsoProvider>;
};
